import React, { useState, useEffect } from "react";
import { FaClock, FaExclamationCircle, FaShieldAlt, FaIdCard, FaTimes, FaCheck, FaGem, FaChevronDown, FaChevronUp } from "react-icons/fa";
import VerifiedBadge from "./VerifiedBadge";
import { getVerificationStatus, applyVerification } from "../api/userApi";
import { getOrangeTickPlans, createPaymentOrder, verifyPayment } from "../api/subscriptionApi";

const ProfileVerificationSection = ({ userProfile }) => {
  const [verifStatus, setVerifStatus] = useState(userProfile?.verification || { status: "NOT_VERIFIED", isVerified: false });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  // Orange Tick Verification Plans state
  const [orangeTickPlans, setOrangeTickPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [expandedPlans, setExpandedPlans] = useState({});

  const toggleExpandPlan = (planId) => {
    setExpandedPlans((prev) => ({ ...prev, [planId]: !prev[planId] }));
  };

  // Form State
  const [formData, setFormData] = useState({
    fullName: userProfile?.name || "",
    username: userProfile?.username || "",
    governmentIdType: "Aadhar",
    governmentIdNumber: "",
    website: "",
    instagram: "",
    facebook: "",
    youtube: "",
    twitter: "",
    linkedin: "",
    reason: "",
    confirmation: false,
  });

  // Files
  const [idFrontFile, setIdFrontFile] = useState(null);
  const [idBackFile, setIdBackFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);

  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchPlans();

    // Load Razorpay Checkout script dynamically
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await getVerificationStatus();
      if (res && res.success) {
        setVerifStatus(res.verification || { status: "NOT_VERIFIED", isVerified: false });
      }
    } catch (err) {
      console.error("Fetch verification status error:", err);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await getOrangeTickPlans();
      if (res && res.success) {
        const fetched = res.plans || [];
        setOrangeTickPlans(fetched);
        if (fetched.length > 0) {
          setSelectedPlanId(fetched[0]._id);
        }
      }
    } catch (err) {
      console.error("Fetch orange tick plans error:", err);
      setOrangeTickPlans([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleOpenApplyModalWithPlan = (planId) => {
    if (planId) setSelectedPlanId(planId);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    if (!formData.fullName.trim()) {
      setFeedback({ type: "error", message: "Full Name is required." });
      return;
    }
    if (!idFrontFile) {
      setFeedback({ type: "error", message: "Front ID Image is required." });
      return;
    }
    if (!selfieFile) {
      setFeedback({ type: "error", message: "Selfie Photo is required." });
      return;
    }
    if (!formData.confirmation) {
      setFeedback({ type: "error", message: "Please confirm that the submitted documents are genuine." });
      return;
    }

    const selectedPlan = orangeTickPlans.find((p) => p._id === selectedPlanId);

    // Helper to submit verification request with optional payment details
    const submitVerificationRequest = async (paymentDetails = {}) => {
      try {
        const body = new FormData();
        body.append("fullName", formData.fullName.trim());
        body.append("username", formData.username.trim());
        body.append("governmentIdType", formData.governmentIdType);
        body.append("governmentIdNumber", formData.governmentIdNumber.trim());
        body.append("website", formData.website.trim());
        body.append("instagram", formData.instagram.trim());
        body.append("facebook", formData.facebook.trim());
        body.append("youtube", formData.youtube.trim());
        body.append("twitter", formData.twitter.trim());
        body.append("linkedin", formData.linkedin.trim());
        body.append("reason", formData.reason.trim());
        body.append("confirmation", "true");

        if (selectedPlanId) {
          body.append("planId", selectedPlanId);
        }

        if (paymentDetails.paymentId) {
          body.append("paymentId", paymentDetails.paymentId);
        }
        if (paymentDetails.orderId) {
          body.append("orderId", paymentDetails.orderId);
        }

        body.append("idFront", idFrontFile);
        if (idBackFile) body.append("idBack", idBackFile);
        body.append("selfie", selfieFile);

        const res = await applyVerification(body);
        if (res && res.success) {
          setFeedback({ type: "success", message: "Verification request submitted successfully!" });
          setIsModalOpen(false);
          fetchStatus();
        } else {
          setFeedback({ type: "error", message: res.message || "Failed to submit verification request." });
        }
      } catch (err) {
        console.error("Submit verification error:", err);
        const msg = err.response?.data?.message || "Failed to submit verification request.";
        setFeedback({ type: "error", message: msg });
      } finally {
        setIsSubmitting(false);
      }
    };

    setIsSubmitting(true);

    // Check if selected plan requires payment (> 0 rupees)
    if (selectedPlan && selectedPlan.price > 0) {
      if (!window.Razorpay) {
        setFeedback({ type: "error", message: "Payment SDK failed to load. Please check your internet connection." });
        setIsSubmitting(false);
        return;
      }

      try {
        const orderRes = await createPaymentOrder({ planId: selectedPlanId });
        if (!orderRes || !orderRes.success) {
          setFeedback({ type: "error", message: orderRes?.message || "Failed to initialize payment order." });
          setIsSubmitting(false);
          return;
        }

        const options = {
          key: orderRes.key,
          amount: orderRes.order.amount,
          currency: "INR",
          name: "CatchWatch Verification",
          description: `Orange Tick Badge - ${selectedPlan.name}`,
          order_id: orderRes.order.id,
          handler: async function (response) {
            try {
              const verifyRes = await verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                planId: selectedPlanId,
              });

              if (verifyRes && verifyRes.success) {
                // Payment verified, now submit verification request
                await submitVerificationRequest({
                  paymentId: response.razorpay_payment_id,
                  orderId: response.razorpay_order_id,
                });
              } else {
                setFeedback({ type: "error", message: verifyRes?.message || "Payment verification failed." });
                setIsSubmitting(false);
              }
            } catch (err) {
              console.error("Verification payment error:", err);
              setFeedback({ type: "error", message: "Payment verification failed. Please contact support." });
              setIsSubmitting(false);
            }
          },
          modal: {
            ondismiss: function () {
              setIsSubmitting(false);
              setFeedback({ type: "error", message: "Payment was cancelled." });
            },
          },
          prefill: {
            name: formData.fullName || userProfile?.name || "User",
            contact: userProfile?.phone || "9999999999",
          },
          theme: {
            color: "#F97316",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } catch (err) {
        console.error("Create verification order error:", err);
        setFeedback({ type: "error", message: err.response?.data?.message || "Failed to initialize payment." });
        setIsSubmitting(false);
      }
    } else {
      // Free plan or no plan required
      await submitVerificationRequest();
    }
  };


  const status = verifStatus.status || "NOT_VERIFIED";

  const isAlreadyVerified = Boolean(
    userProfile?.isVerified ||
    verifStatus?.isVerified ||
    verifStatus?.status === "APPROVED" ||
    userProfile?.verification?.status === "APPROVED"
  );
  const isPendingReview = verifStatus?.status === "PENDING";

  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl relative overflow-hidden">
      {/* Accent Gradient Border Line Top */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brand-orange via-amber-500 to-orange-600"></div>

      {/* Header Info Banner */}
      <div className="flex items-start justify-between flex-wrap gap-4 border-b border-gray-100 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-orange-50 text-brand-orange flex items-center justify-center text-base">
              <FaShieldAlt />
            </div>
            <h2 className="text-lg font-black text-gray-900 tracking-tight">
              Profile Verification
            </h2>
          </div>
          <p className="text-xs font-semibold text-gray-500 mt-1">
            Get verified to build authenticity and unlock exclusive creator benefits.
          </p>
        </div>

        {/* Current Badge Status Indicator */}
        <div>
          {isAlreadyVerified ? (
            <div className="flex items-center gap-2 bg-orange-50 text-orange-700 border border-orange-200 px-3.5 py-1.5 rounded-full text-xs font-extrabold shadow-sm">
              <VerifiedBadge isVerified={true} size="md" />
              <span>Verified Account</span>
            </div>
          ) : status === "PENDING" ? (
            <div className="flex items-center gap-2 bg-yellow-50 text-yellow-700 border border-yellow-200 px-3.5 py-1.5 rounded-full text-xs font-extrabold shadow-sm">
              <FaClock className="text-yellow-500 animate-spin" />
              <span>Under Review</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-gray-50 text-gray-600 border border-gray-200 px-3.5 py-1.5 rounded-full text-xs font-bold">
              <span>Unverified Account</span>
            </div>
          )}
        </div>
      </div>

      {/* Status Banner Message */}
      <div className="mt-5">
        {status === "PENDING" && (
          <div className="bg-amber-50/70 border border-amber-200/80 rounded-2xl p-4 flex items-start gap-3 text-amber-900">
            <FaClock className="text-amber-500 mt-0.5 flex-shrink-0 text-base" />
            <div>
              <h4 className="text-xs font-black uppercase tracking-wider text-amber-800">
                Verification Application Under Review
              </h4>
              <p className="text-xs font-medium text-amber-700 mt-0.5">
                Our administration team is reviewing your identification documents. Response usually takes 24-48 hours.
              </p>
            </div>
          </div>
        )}

        {status === "REJECTED" && (
          <div className="bg-red-50/70 border border-red-200/80 rounded-2xl p-4 flex items-start gap-3 text-red-900">
            <FaExclamationCircle className="text-red-500 mt-0.5 flex-shrink-0 text-base" />
            <div>
              <h4 className="text-xs font-black uppercase tracking-wider text-red-800">
                Verification Application Rejected
              </h4>
              <p className="text-xs font-medium text-red-700 mt-0.5">
                {verifStatus.rejectionReason
                  ? `Reason: ${verifStatus.rejectionReason}`
                  : "Your submitted documents did not meet our verification criteria. You may select a plan and re-apply."}
              </p>
            </div>
          </div>
        )}

        {status === "SUSPENDED" && (
          <div className="bg-purple-50/70 border border-purple-200/80 rounded-2xl p-4 flex items-start gap-3 text-purple-900">
            <FaExclamationCircle className="text-purple-500 mt-0.5 flex-shrink-0 text-base" />
            <div>
              <h4 className="text-xs font-black uppercase tracking-wider text-purple-800">
                Verification Suspended
              </h4>
              <p className="text-xs font-medium text-purple-700 mt-0.5">
                {verifStatus.suspensionReason
                  ? `Reason: ${verifStatus.suspensionReason}`
                  : "Your orange tick status has been temporarily suspended."}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Active Verified Member Card (Shown when user has active verification / plan) ── */}
      {isAlreadyVerified && (
        <div className="mt-6 pt-2">
          <div className="bg-gradient-to-r from-brand-orange to-amber-600 text-white rounded-2xl p-5 shadow-lg border border-orange-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-xl font-black">
                  ✓
                </div>
                <div>
                  <h4 className="text-base font-black flex items-center gap-2">
                    <span>Active Verified Profile</span>
                    <VerifiedBadge isVerified={true} size="md" />
                  </h4>
                  <p className="text-xs text-orange-100 font-medium mt-0.5">
                    Your profile is officially authenticated & protected on CatchWatch.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-white/20 grid grid-cols-2 gap-2.5 text-xs font-bold text-orange-50">
              <div className="flex items-center gap-1.5">⚡ Search Ranking Boost</div>
              <div className="flex items-center gap-1.5">🛡️ Impersonation Protection</div>
              <div className="flex items-center gap-1.5">💬 Pinned & Highlighted Comments</div>
              <div className="flex items-center gap-1.5">🎧 VIP Creator Support Desk</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Orange Tick Verification Plans Section (Only shown for unverified users who have not applied) ── */}
      {!isAlreadyVerified && !isPendingReview && orangeTickPlans.length > 0 && (
        <div className="mt-6 pt-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
              <FaGem className="text-brand-orange" />
              <span>Orange Tick & Creator Verification Tiers</span>
            </h3>
            <span className="text-xs text-brand-orange font-bold bg-orange-50 px-2.5 py-1 rounded-full">
              Official Orange Badge
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-1 gap-4">
            {orangeTickPlans.map((plan) => (
              <div
                key={plan._id}
                className={`relative bg-gradient-to-b from-orange-50/40 to-white border-2 rounded-2xl p-5 flex flex-col justify-between transition hover:shadow-md ${
                  plan.isRecommended
                    ? "border-orange-500 ring-2 ring-orange-500/20"
                    : "border-gray-200 hover:border-orange-300"
                }`}
              >
                {plan.isRecommended && (
                  <span className="absolute -top-3 left-4 bg-brand-orange text-white text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full shadow-sm">
                    Recommended
                  </span>
                )}

                <div>
                  <div className="flex items-center justify-between">
                    <h4 className="text-base font-black text-gray-900">{plan.name}</h4>
                    <VerifiedBadge isVerified={true} size="md" />
                  </div>

                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-2xl font-black text-brand-orange">₹{plan.price}</span>
                    <span className="text-xs font-bold text-gray-500">/ {plan.duration} days</span>
                  </div>

                  {/* Features */}
                  {plan.features && plan.features.length > 0 && (
                    <div className="mt-4 space-y-2 border-t border-gray-100 pt-3">
                      {(expandedPlans[plan._id] ? plan.features : plan.features.slice(0, 3)).map((feat, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs font-bold text-gray-700">
                          <FaCheck className="text-brand-orange mt-0.5 flex-shrink-0" />
                          <span>{feat}</span>
                        </div>
                      ))}
                      {plan.features.length > 3 && (
                        <button
                          type="button"
                          onClick={() => toggleExpandPlan(plan._id)}
                          className="mt-2 text-xs font-black text-brand-orange hover:text-orange-700 flex items-center gap-1 focus:outline-none transition"
                        >
                          {expandedPlans[plan._id] ? (
                            <>
                              <span>Show Less</span>
                              <FaChevronUp className="text-[10px]" />
                            </>
                          ) : (
                            <>
                              <span>+ Read More ({plan.features.length - 3} more features)</span>
                              <FaChevronDown className="text-[10px]" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <button
                    onClick={() => handleOpenApplyModalWithPlan(plan._id)}
                    className="w-full mt-5 py-2.5 bg-brand-orange hover:bg-orange-600 text-white font-extrabold text-xs rounded-xl transition shadow-sm"
                  >
                    Select & Apply
                  </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification Benefits Grid */}
      <div className="mt-8 pt-6 border-t border-gray-100">
        <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-4">
          Verification Benefits
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 gap-3.5">
          {[
            {
              title: "Official Verified Badge",
              desc: "Orange Tick displayed everywhere across CatchWatch.",
              icon: "🧡",
            },
            {
              title: "Advanced Account Protection",
              desc: "Enhanced multi-layer security for authenticated profiles.",
              icon: "🔒",
            },
            {
              title: "Fake Account Protection",
              desc: "Proactive detection & removal of impersonating accounts.",
              icon: "🛡️",
            },
            {
              title: "Priority Customer Support",
              desc: "Dedicated priority queue & fast resolution for support tickets.",
              icon: "🎧",
            },
            {
              title: "Higher Search Ranking",
              desc: "Verified creators appear higher in search & discover feeds.",
              icon: "⚡",
            },
            {
              title: "Better Content Recommendations",
              desc: "Algorithmic boost to feature your reels & videos to a broader audience.",
              icon: "🌟",
            },
            {
              title: "Highlighted Comments",
              desc: "Your comments stand out with an orange tick badge and top placement.",
              icon: "💬",
            },
            {
              title: "Exclusive Creator Tools",
              desc: "Access special creator analytics, monetization tools & badges.",
              icon: "🛠️",
            },
            {
              title: "Early Access to New Features",
              desc: "Try new platform features & experimental tools before everyone else.",
              icon: "🚀",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="bg-gray-50/80 hover:bg-orange-50/40 border border-gray-100 hover:border-orange-200/80 p-4 rounded-2xl transition flex items-start gap-3 group shadow-xs"
            >
              {/* <div className="w-8 h-8 rounded-xl bg-orange-100/70 text-brand-orange flex items-center justify-center font-black text-sm flex-shrink-0 group-hover:bg-brand-orange group-hover:text-white transition">
                {item.icon}
              </div> */}
              <div>
                <h4 className="text-xs font-black text-gray-900 flex items-center gap-1.5">
                  <span>{item.title}</span>
                </h4>
                <p className="text-[11px] text-gray-500 font-medium mt-1 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Apply Verification Modal Form ── */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl relative my-8">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-6">
              <div>
                <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                  <FaIdCard className="text-brand-orange" />
                  <span>Apply for Profile Verification</span>
                </h3>
                <p className="text-xs text-gray-500 font-medium mt-0.5">
                  Select a verification tier and submit genuine documents to request your official orange tick.
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition"
              >
                <FaTimes />
              </button>
            </div>

            {feedback.message && (
              <div
                className={`p-3.5 rounded-xl text-xs font-bold mb-5 ${
                  feedback.type === "success"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-red-50 text-red-600 border border-red-200"
                }`}
              >
                {feedback.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5 text-left">
              {/* Plan Selection in Modal */}
              {orangeTickPlans.length > 0 && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2">
                    Select Verification Plan <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {orangeTickPlans.map((plan) => (
                      <div
                        key={plan._id}
                        onClick={() => setSelectedPlanId(plan._id)}
                        className={`p-3.5 rounded-2xl border-2 cursor-pointer transition flex flex-col justify-between ${
                          selectedPlanId === plan._id
                            ? "border-orange-500 bg-orange-50/50 ring-2 ring-orange-500/20"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-black text-gray-900">{plan.name}</span>
                          <VerifiedBadge isVerified={true} size="sm" />
                        </div>
                        <div className="text-lg font-black text-brand-orange">₹{plan.price}</div>
                        <div className="text-[11px] font-bold text-gray-400">{plan.duration} days validity</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    placeholder="Enter your official full name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-brand-orange"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    name="username"
                    placeholder="@username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-brand-orange"
                  />
                </div>
              </div>

              {/* ID Type & Number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Government ID Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="governmentIdType"
                    value={formData.governmentIdType}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-brand-orange bg-white"
                  >
                    <option value="Aadhar">Aadhar Card</option>
                    <option value="Passport">Passport</option>
                    <option value="Driving License">Driving License</option>
                    <option value="PAN">PAN Card</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Government ID Number (Optional)</label>
                  <input
                    type="text"
                    name="governmentIdNumber"
                    placeholder="Enter ID document number"
                    value={formData.governmentIdNumber}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-brand-orange"
                  />
                </div>
              </div>

              {/* Document Uploads */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Front ID Image <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center bg-gray-50/50 hover:bg-orange-50/30 transition cursor-pointer relative">
                    <input
                      type="file"
                      accept="image/*"
                      required
                      onChange={(e) => handleFileChange(e, setIdFrontFile, setIdFrontPreview)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    {idFrontPreview ? (
                      <img src={idFrontPreview} alt="Front ID" className="h-20 w-full object-cover rounded-lg" />
                    ) : (
                      <div className="py-2 text-xs font-bold text-gray-400">Upload Front Image</div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Back ID Image (Optional)</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center bg-gray-50/50 hover:bg-orange-50/30 transition cursor-pointer relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, setIdBackFile, setIdBackPreview)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    {idBackPreview ? (
                      <img src={idBackPreview} alt="Back ID" className="h-20 w-full object-cover rounded-lg" />
                    ) : (
                      <div className="py-2 text-xs font-bold text-gray-400">Upload Back Image</div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Selfie Photo <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center bg-gray-50/50 hover:bg-orange-50/30 transition cursor-pointer relative">
                    <input
                      type="file"
                      accept="image/*"
                      required
                      onChange={(e) => handleFileChange(e, setSelfieFile, setSelfiePreview)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    {selfiePreview ? (
                      <img src={selfiePreview} alt="Selfie" className="h-20 w-full object-cover rounded-lg" />
                    ) : (
                      <div className="py-2 text-xs font-bold text-gray-400">Upload Selfie</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Social Links (Optional) */}
              <div>
                <label className="block text-xs font-extrabold text-gray-400 uppercase tracking-wider mb-2">
                  Social Links & Media Presence (Optional)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="url"
                    name="website"
                    placeholder="Website URL"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="p-2.5 border border-gray-200 rounded-xl text-xs font-semibold"
                  />
                  <input
                    type="text"
                    name="instagram"
                    placeholder="Instagram handle/link"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    className="p-2.5 border border-gray-200 rounded-xl text-xs font-semibold"
                  />
                  <input
                    type="text"
                    name="facebook"
                    placeholder="Facebook profile/page"
                    value={formData.facebook}
                    onChange={handleInputChange}
                    className="p-2.5 border border-gray-200 rounded-xl text-xs font-semibold"
                  />
                  <input
                    type="text"
                    name="youtube"
                    placeholder="YouTube Channel link"
                    value={formData.youtube}
                    onChange={handleInputChange}
                    className="p-2.5 border border-gray-200 rounded-xl text-xs font-semibold"
                  />
                  <input
                    type="text"
                    name="twitter"
                    placeholder="X / Twitter handle"
                    value={formData.twitter}
                    onChange={handleInputChange}
                    className="p-2.5 border border-gray-200 rounded-xl text-xs font-semibold"
                  />
                  <input
                    type="text"
                    name="linkedin"
                    placeholder="LinkedIn Profile link"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    className="p-2.5 border border-gray-200 rounded-xl text-xs font-semibold"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Reason for Verification</label>
                <textarea
                  name="reason"
                  rows="2"
                  placeholder="Explain why your account should be verified..."
                  value={formData.reason}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:border-brand-orange"
                />
              </div>

              {/* Checkbox confirmation */}
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="confirmation"
                  name="confirmation"
                  checked={formData.confirmation}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-brand-orange rounded border-gray-300 focus:ring-brand-orange"
                />
                <label htmlFor="confirmation" className="text-xs font-bold text-gray-700 cursor-pointer">
                  I confirm that the submitted documents are genuine.
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 bg-brand-orange hover:bg-orange-600 text-white font-extrabold text-xs rounded-xl transition disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting Request..." : "Submit & Apply Verification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileVerificationSection;
